import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import {
  Dialog,
  DialogHeading,
  PrimaryCTA,
  SecondaryCTA,
  Text,
  TranslatedText,
} from '@pmu/tornado';

import { useResendConfirmationEmailMutation } from '@api/services';
import { useAppDispatch } from '@store/hooks';
import { actions as toastActions } from '@store/slices/toastSlice';

import './index.scss';

interface FirstLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/;

const FirstLoginModal = ({ isOpen, onClose }: FirstLoginModalProps) => {
  const intl = useIntl();

  const dispatch = useAppDispatch();
  const [email, setEmail] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const [resendConfirmationEmail, { isLoading, isError, reset }] =
    useResendConfirmationEmailMutation();

  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setHasSubmitted(false);
      reset();
    }
  }, [isOpen]);

  const trimmedEmail = email.trim();
  const isEmailFormatInvalid = hasSubmitted && !EMAIL_REGEX.test(trimmedEmail);
  const isValidateDisabled = trimmedEmail.length === 0;

  const handleValidate = async () => {
    setHasSubmitted(true);

    if (!EMAIL_REGEX.test(trimmedEmail)) {
      return;
    }

    const result = await resendConfirmationEmail(trimmedEmail);
    if (!('error' in result)) {
      dispatch(
        toastActions.showToast({
          children: <TranslatedText id="app.sidebar.firstLoginModal.success" />,
        })
      );
      onClose();
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      setOpen={onClose}
      showDismiss={false}
      hideOnInteractOutside={false}
      hideOnEscape={false}
    >
      <DialogHeading>
        <Text size="xl">
          <TranslatedText id="app.sidebar.firstLoginModal.title" />
        </Text>
      </DialogHeading>

      <Text
        as="p"
        size="m"
        color="gray-700"
        className="layout-sideBarMenu-FirstLoginModal-subtitle"
      >
        <TranslatedText id="app.sidebar.firstLoginModal.subtitle" />
      </Text>

      <div className="layout-sideBarMenu-FirstLoginModal-field">
        <Text size="sm" color="gray-700" type="bold">
          <TranslatedText id="app.sidebar.firstLoginModal.email.label" />
        </Text>
        <input
          type="email"
          aria-label={intl.formatMessage({ id: 'app.sidebar.firstLoginModal.email.label' })}
          value={email}
          onChange={e => {
            setEmail(e.target.value);
            setHasSubmitted(false);
          }}
          placeholder={intl.formatMessage({ id: 'app.sidebar.firstLoginModal.email.label' })}
          className={classNames('layout-sideBarMenu-FirstLoginModal-input', {
            'layout-sideBarMenu-FirstLoginModal-input--error': isEmailFormatInvalid,
          })}
        />
        {isEmailFormatInvalid ? (
          <Text as="span" color="red-notification" size="sm">
            <TranslatedText id="app.sidebar.firstLoginModal.email.error" />
          </Text>
        ) : null}
      </div>

      {isError ? (
        <Text as="p" color="red-notification" size="sm">
          <TranslatedText id="app.generic.errorMessage" />
        </Text>
      ) : null}

      <div className="layout-sideBarMenu-FirstLoginModal-actions">
        <SecondaryCTA onClick={handleCancel}>
          <Text size="m">
            <TranslatedText id="app.sidebar.firstLoginModal.cancel" />
          </Text>
        </SecondaryCTA>
        <PrimaryCTA onClick={handleValidate} disabled={isValidateDisabled || isLoading}>
          <Text size="m">
            <TranslatedText id="app.sidebar.firstLoginModal.validate" />
          </Text>
        </PrimaryCTA>
      </div>
    </Dialog>
  );
};

export { FirstLoginModal };
